/*
 * Obraven product page helpers (sections/main-product.liquid):
 * - <product-sticky-bar>: mobile bar with a quantity stepper mirrored to the main form and an
 *   "Add to bag · size" button; shown below 750px once the main button has scrolled away.
 * - <delivery-estimate>: "Order today for delivery by {date}" from the store's cut-off hour and
 *   working days (Theme settings › Store information), skipping weekends.
 */
if (!customElements.get('product-sticky-bar')) {
  customElements.define(
    'product-sticky-bar',
    class ProductStickyBar extends HTMLElement {
      connectedCallback() {
        this.mainInput = document.querySelector(this.dataset.quantityInput);
        this.input = this.querySelector('[data-sticky-quantity]');
        this.mobile = window.matchMedia('(max-width: 749px)');
        this.visible = false;

        this.querySelectorAll('[data-sticky-step]').forEach((button) =>
          button.addEventListener('click', () => this.step(parseInt(button.dataset.stickyStep, 10)))
        );
        this.input?.addEventListener('change', () => this.setQuantity(parseInt(this.input.value, 10)));
        this.mainInput?.addEventListener('change', () => this.syncFromMain());
        this.syncFromMain();

        this.watched = document.querySelector(this.dataset.watch);
        if (!this.watched) return;
        // A scroll check (not IntersectionObserver): a fast scroll can jump the button from below
        // the fold to above it without ever intersecting.
        this.onScroll = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.update();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.mobile.addEventListener('change', this.onScroll);
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.mobile?.removeEventListener('change', this.onScroll);
        document.body.classList.remove('ob-sticky-bar-visible');
      }

      update() {
        const pastButton = this.watched.getBoundingClientRect().bottom < 0;
        const show = Boolean(pastButton && this.mobile.matches);
        if (show === this.visible) return;
        this.visible = show;
        this.classList.toggle('is-visible', show);
        this.toggleAttribute('inert', !show);
        this.setAttribute('aria-hidden', String(!show));
        document.body.classList.toggle('ob-sticky-bar-visible', show);
      }

      step(delta) {
        const step = parseInt(this.mainInput?.step, 10) || 1;
        this.setQuantity((parseInt(this.input.value, 10) || 1) + delta * step);
      }

      setQuantity(value) {
        const min = parseInt(this.mainInput?.min, 10) || 1;
        const max = parseInt(this.mainInput?.max, 10) || Infinity;
        const quantity = Math.min(Math.max(Number.isNaN(value) ? min : value, min), max);
        this.input.value = quantity;
        if (this.mainInput && String(this.mainInput.value) !== String(quantity)) {
          this.mainInput.value = quantity;
          this.mainInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.updateButtons(quantity, min, max);
      }

      syncFromMain() {
        if (!this.input) return;
        const value = parseInt(this.mainInput?.value, 10) || 1;
        this.input.value = value;
        this.updateButtons(value, parseInt(this.mainInput?.min, 10) || 1, parseInt(this.mainInput?.max, 10) || Infinity);
      }

      updateButtons(value, min, max) {
        const minus = this.querySelector('[data-sticky-step="-1"]');
        const plus = this.querySelector('[data-sticky-step="1"]');
        minus?.classList.toggle('disabled', value <= min);
        plus?.classList.toggle('disabled', value >= max);
      }
    }
  );
}

if (!customElements.get('delivery-estimate')) {
  customElements.define(
    'delivery-estimate',
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        const date = this.estimate();
        if (!date) return;

        let formatted;
        try {
          formatted = new Intl.DateTimeFormat(this.dataset.locale || undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            timeZone: 'UTC',
          }).format(date);
        } catch {
          formatted = date.toUTCString().slice(0, 11);
        }

        const span = document.createElement('span');
        span.textContent = formatted;
        this.innerHTML = (this.dataset.template || '%DATE%').replace('%DATE%', span.innerHTML);
        const row = this.closest('[data-delivery-row]');
        if (row) row.hidden = false;
      }

      // Returns the delivery date as a UTC date holding the store's local calendar day.
      estimate() {
        const match = /^([+-])(\d{2})(\d{2})$/.exec(this.dataset.utcOffset || '+0000');
        if (!match) return null;
        const offsetMinutes = (match[1] === '-' ? -1 : 1) * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
        const storeNow = new Date(Date.now() + offsetMinutes * 60000);
        const cutoff = parseInt(this.dataset.cutoff, 10);
        const days = Math.max(parseInt(this.dataset.days, 10) || 1, 1);
        const isWeekend = (day) => day.getUTCDay() === 0 || day.getUTCDay() === 6;

        const date = new Date(Date.UTC(storeNow.getUTCFullYear(), storeNow.getUTCMonth(), storeNow.getUTCDate()));
        // Dispatch today before the cut-off on a working day, otherwise on the next working day.
        if (isWeekend(date) || storeNow.getUTCHours() >= cutoff) {
          do {
            date.setUTCDate(date.getUTCDate() + 1);
          } while (isWeekend(date));
        }
        for (let added = 0; added < days; ) {
          date.setUTCDate(date.getUTCDate() + 1);
          if (!isWeekend(date)) added += 1;
        }
        return date;
      }
    }
  );
}
